<script>

	import configuration from './configuration.json';
	import persistent from './data/persistent.svelte.js';
	import { loadSession, initializeSession, isChatAssisted, currentSession, currentSessionId } from './data/session.svelte.js';
	import app from './data/app.svelte.js';

    import { _, locale, addMessages } from 'svelte-i18n';
	import nlInformal from './locale/nl-informal.json';
	import { Router } from "@mateothegreat/svelte5-router";
    import { goto, replace, getFirstIncompleteStep } from './helpers/navigation.svelte.js';
	import routes from './routes';
	import { initializeTracking, trackPageView, trackStep } from './helpers/tracking.svelte.js';
	import { initializeReserve } from './helpers/reserve.svelte.js';
	import { registerBookingTools } from './helpers/webmcp.svelte.js';
	import { getContrastColor, getAccentContrast } from './helpers/color.js';

	import Icon from './components/Icon.svelte';
    import protectIcon from './assets/icons/protect.svg?raw';

	import Widget from './components/Widget.svelte'
	import Badge from './components/Badge.svelte'
    import Alert from './components/Alert.svelte'
	import Section from './components/Section.svelte'
	import Loader from './components/Loader.svelte'
    import CookieBanner from './components/CookieBanner.svelte';
    import ConnectionError from './components/ConnectionError.svelte';
    import { fetchApi, ApiError, registerConnectionErrorDialog } from './helpers/api.svelte.js';

	import getScrollbarWidth from 'get-scrollbar-width';
    import { get } from 'svelte/store';



	/* Force initialization of persistent GUID */

	persistent.guid;


	/* Start session on the server */

	async function startSession(client, salon) {
		try {
			const form = new FormData();
			form.append('session', currentSessionId);
			form.append('client', client);
			form.append('salon', salon);
			form.append('guid', persistent.guid);
			form.append('application', 'nl.salonhub.afspraak');
			form.append('assistant', isChatAssisted());

			await fetch(`${configuration.endpoint}/OnlineAppointment.Remote.Session/start`, {
				method: 'POST',
				body: form,
				headers: {
					'Authorization': `Bearer ${configuration.apikey}`
				}
			});
		} catch (error) {
			// Silently ignore errors - session start is not critical for booking flow
		}
	}


	/* Setup animation direction for pop state */

	window.addEventListener('popstate', () => {
		app.state.direction = 'left';
	});


	/* Setup overlay or tracked scrollbar width */

	document.body.style.setProperty('--scrollbar-width', `${getScrollbarWidth()}px`);


	/* Get salon settings */

	async function getSalon(client, salon) {
		const data = await fetchApi({
			endpoint: 'OnlineAppointment.Remote.Salons/get',
			params: { client, salon }
		});

		if (!data) return;

		Object.assign(app, data);
		setupFormality();
		setupTheme();
		setupTracking();
		setupValidation();
		showCookieBannerIfNeeded();
	}

	/* Set up formality (informal Dutch overrides) */

	function setupFormality() {
		if (app.settings.formality === 'informal') {
			addMessages('nl', nlInformal);
		}
	}

	/* Set up theme */

	function setupTheme() {
		const accent = app.settings.theme.colors.accent;
		document.body.style.setProperty('--accent-background', accent);
		document.body.style.setProperty('--accent-color', getContrastColor(accent));
		document.body.style.setProperty('--accent-contrast', getAccentContrast(accent));
		document.body.dataset.themeBackgroundPattern = app.settings.theme.background.pattern;
		document.body.dataset.themeTypographyFont = app.settings.theme.typography.font;

		if (app.settings.theme.background.pattern == 'custom') {
			if (app.settings.theme.background.image?.url) {
				let actualWidth = window.innerWidth * window.devicePixelRatio;
				let imageWidth = 1280;

				if (actualWidth > 1920) {
					imageWidth = 1920;
				}

				document.body.style.setProperty('background-image', `url(${app.settings.theme.background.image.url}?w${imageWidth})`);
			}
		}
	}

	/* Set up tracking */

	function setupTracking() {
		if (persistent.cookies) {
			initializeTracking(currentSession.params);
		}
	}

	/* Set up validation timing */

	function setupValidation() {
		// Start with default value from settings
		let validate = app.settings.customer.step || 'late';

		// Customer stop: only existing customers allowed => force early
		if (app.settings.customer.allow === 'existing') {
			validate = 'early';
		}

		// Check if origin is Google Reserve => late (overrides customer stop)
		if (currentSession.origin === 'google-reserve') {
			validate = 'late';
		}

		currentSession.validate = validate;
	}

	/* Show cookie banner if needed */

	function showCookieBannerIfNeeded() {
		// Don't ask again if user already made a choice during this session
		if (currentSession.consent) {
			return;
		}

		const isGoogleReserveOrigin = currentSession.origin === 'google-reserve';

		let cookieConsentRequired = !persistent.cookies &&
			(app.settings.widget.analytics.enabled || app.settings.widget.ads.enabled || isGoogleReserveOrigin);

		/* Debug override */
		// cookieConsentRequired = true;

		if (cookieConsentRequired) {
			cookieBanner?.show();
		}
	}

	function handleCookieAccept() {
		currentSession.consent = true;
		persistent.cookies = true;
		initializeTracking(currentSession.params);
	}

	function handleCookieDecline() {
		currentSession.consent = true;
		persistent.cookies = false;
	}

	/*
		Post navigation hook to track page views
	*/

	let lastTrackedPath = '';

	function trackingHook(route) {
		const currentPath = route.result.path.original;

		// Track the current step (last path segment) for the launcher visibility, and
		// flag the chat route on <html> so the layout can fill/lock the screen (app.css).
		app.state.step = currentPath.split('/').filter(Boolean).pop() || null;
		document.documentElement.dataset.chat = app.state.step === 'chat' ? 'true' : '';

		// Deduplicate tracking due to svelte5-router calling post hooks twice
		// (router sets this.current after post hooks run, triggering re-evaluation)

		if (currentPath !== lastTrackedPath) {
			lastTrackedPath = currentPath;

			const sanitizedPath = currentPath.replace(/\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i, '/...');
			trackPageView(sanitizedPath);

			if (route.route?.name) {
				trackStep(route.route.name);
			}
		}

		return true;
	}

	/*
		Auth guard for all routes,
		creates a session if needed and
		ensures the session is valid
	*/

	let initialized = $state(false);
	let loading = $state(true);
	let cookieBanner;
	let connectionError;

	$effect(() => {
		if (connectionError) {
			registerConnectionErrorDialog(connectionError);
		}
	});

	/* Flag the on-screen keyboard on <body> (.keyboard-open). The keyboard shrinks the
	   visual viewport without changing the layout viewport, so it's visible when the two
	   diverge by more than a threshold (large enough to ignore the URL bar). Purely a
	   detection hook — it does NOT move the layout (iOS handles that natively). The chat
	   uses it to drop the badge's bottom safe-area so the composer sits directly above
	   the keyboard. */
	$effect(() => {
		const vv = window.visualViewport;
		if (!vv) return;

		// Reference = the tallest viewport seen so far (no keyboard). Comparing the visual
		// viewport against this is robust to iOS builds where window.innerHeight shrinks
		// WITH the keyboard (so innerHeight - vv.height would stay ~0). The 150px threshold
		// ignores URL-bar show/hide.
		let baseline = 0;
		const update = () => {
			baseline = Math.max(baseline, window.innerHeight, vv.height);
			document.body.classList.toggle('keyboard-open', baseline - vv.height > 150);
		};
		update();
		vv.addEventListener('resize', update);
		vv.addEventListener('scroll', update);

		return () => {
			vv.removeEventListener('resize', update);
			vv.removeEventListener('scroll', update);
			document.body.classList.remove('keyboard-open');
		};
	});

	/* Expose the booking flow as WebMCP tools once the session/salon is ready */

	$effect(() => {
		if (!initialized) return;
		return registerBookingTools();
	});

	/* Reload salon data when locale changes (for server-translated content like treatmentGroups) */

	let lastLocale = get(locale);

	$effect(() => {
		const currentLocale = $locale;

		if (initialized && currentLocale !== lastLocale) {
			lastLocale = currentLocale;
			getSalon(currentSession.client, currentSession.salon);
		}
	});

	async function authGuard(route) {

		/* Generate new session for entry points without sessionId */

		if (route.result.path.params?.client && route.result.path.params?.salon && !route.result.component) {
			initializeSession({
				client: route.result.path.params.client,
				salon: route.result.path.params.salon
			});

			// Add preset treatment if provided
			if (currentSession.params.treatment) {
				currentSession.preset = {
					treatment: currentSession.params.treatment
				};
			}

			// Start the booking session on the server (fire and forget)
			startSession(currentSession.client, currentSession.salon);

			// Check for Google Reserve token in URL and initialize if present
			if (currentSession.params.rwg_token) {
				initializeReserve(currentSession.params.rwg_token);
			}
			else if (currentSession.params.origin === 'google-reserve') {
				currentSession.origin = 'google-reserve';
			}

			// Redirect to verify page if this is a verification link, otherwise initialize
			if (route.result.path.original.includes('/verify')) {
				goto(`verify${window.location.search}`);
			} else {
				goto('initialize');
			}

			return false;
		}

		/* Initialize salon data */

		if (initialized) {
			/* Check step completeness on every navigation (including browser back/forward) */

			if (route.route?.name) {
				const incompleteStep = getFirstIncompleteStep(route.route.name);

				if (incompleteStep) {
					replace(incompleteStep, 'left');
					return false;
				}
			}

			return true;
		}

		/* Allow error routes without session */

		if (route.result.path.params?.error && !route.result.path.params?.sessionId) {
			loading = false;
			return true;
		}

		/* Load existing session or create new one if expired */

		if (route.result.path.params?.sessionId) {
			const sessionId = route.result.path.params.sessionId;
			const client = route.result.path.params.client;
			const salon = route.result.path.params.salon;
			const session = loadSession(sessionId);

			/* Session expired or lost - create new session with same client/salon */

			if (!session && client && salon) {
				initializeSession({
					client: client,
					salon: salon
				});

				// Start the booking session on the server (fire and forget)
				startSession(client, salon);

				// Redirect to initialize with new session
				goto('initialize');
				return false;
			}

			/* Load salon details */

			if (session) {
				try {
					await getSalon(session.client, session.salon);
				} catch (error) {
					if (error instanceof ApiError) {
						goto('/error/salon-configuration-error');
						return false;
					}
					throw error;
				}
				initialized = true;
				loading = false;
			}
			else {
				// Fallback to error if we don't have client/salon info
				goto('/error/session-not-found');
				return false;
			}

			/* Check if our appointment is already scheduled */

			if (session.status === 'confirmed' || session.status === 'verify') {
				const currentPath = route.result.path.original;

				if (session.status === 'confirmed' && !currentPath.includes('/success')) {
					replace('success');
					return false;
				}

				if (session.status === 'verify' && !currentPath.includes('/verify')) {
					replace('verify');
					return false;
				}
			}

			/* Check step completeness on initial page load */

			if (route.route?.name) {
				const incompleteStep = getFirstIncompleteStep(route.route.name);

				if (incompleteStep) {
					replace(incompleteStep, 'left');
					return false;
				}
			}
		}

		return true;
	}

</script>


<Widget>
	{#if loading}
		<Section>
			<Loader />
		</Section>
	{/if}
	<Router
		{routes}

		hooks={{
			pre: authGuard,
			post: trackingHook
		}}
	/>

	<CookieBanner
		bind:this={cookieBanner}
		onAccept={handleCookieAccept}
		onDecline={handleCookieDecline}
	/>

	<ConnectionError bind:this={connectionError} />
</Widget>
<Badge />
